import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Camera, Upload, FileText, CheckCircle2, AlertCircle,
  Loader2, Image as ImageIcon, X, Zap, Eye
} from 'lucide-react';
import '../styles/mobile.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function MobileScan() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');

  const handleFileSelect = useCallback(async (file) => {
    if (!file || !token) return;

    setError(null);
    setResult(null);
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setScanning(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE}/api/invoice-scanner/scan`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Scanning failed');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message || 'Kunde inte skanna fakturan');
    } finally {
      setScanning(false);
    }
  }, [token]);

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const resetScan = () => {
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const saveAsSupplierInvoice = async () => {
    if (!result || !token) return;

    try {
      const response = await fetch(`${API_BASE}/api/supplier-invoices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          supplier_name: result.supplier_name || '',
          invoice_number: result.invoice_number || '',
          invoice_date: result.invoice_date || new Date().toISOString().split('T')[0],
          due_date: result.due_date || '',
          total_amount: result.total_amount || 0,
          vat_amount: result.vat_amount || 0,
          lines: result.line_items || [],
          notes: 'Skannad via mobilapp'
        })
      });

      if (response.ok) {
        navigate('/m/invoices');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Kunde inte spara fakturan');
    }
  };

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('nav.invoiceScanner')}</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mobile-content">
        {!preview && !result ? (
          <>
            {/* Scan Options */}
            <div className="mobile-card" style={{ textAlign: 'center', padding: '32px 24px' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '20px', 
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2a4d7a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                boxShadow: '0 8px 24px rgba(30, 58, 95, 0.3)'
              }}>
                <Zap size={40} color="white" />
              </div>
              <h2 style={{ marginBottom: '8px', fontSize: '20px' }}>AI Fakturascanner</h2>
              <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '14px' }}>
                Ta en bild av fakturan eller valj en fil. AI:n extraherar automatiskt all information.
              </p>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  className="mobile-btn mobile-btn-primary"
                  onClick={() => cameraInputRef.current?.click()}
                  style={{ flex: 1 }}
                >
                  <Camera size={20} />
                  Ta bild
                </button>
                <button 
                  className="mobile-btn mobile-btn-outline"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ flex: 1 }}
                >
                  <Upload size={20} />
                  Valj fil
                </button>
              </div>

              {/* Hidden inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleInputChange}
                style={{ display: 'none' }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleInputChange}
                style={{ display: 'none' }}
              />
            </div>

            {/* Tips */}
            <div className="mobile-section-header">
              <h3 className="mobile-section-title">Tips for basta resultat</h3>
            </div>
            <div className="mobile-list">
              <div className="mobile-list-item" style={{ cursor: 'default' }}>
                <div className="mobile-list-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                  <CheckCircle2 size={20} />
                </div>
                <div className="mobile-list-content">
                  <div className="mobile-list-title">God belysning</div>
                  <div className="mobile-list-subtitle">Se till att fakturan ar valbeslyst</div>
                </div>
              </div>
              <div className="mobile-list-item" style={{ cursor: 'default' }}>
                <div className="mobile-list-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                  <CheckCircle2 size={20} />
                </div>
                <div className="mobile-list-content">
                  <div className="mobile-list-title">Hela dokumentet</div>
                  <div className="mobile-list-subtitle">Se till att alla kanter syns i bilden</div>
                </div>
              </div>
              <div className="mobile-list-item" style={{ cursor: 'default' }}>
                <div className="mobile-list-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                  <CheckCircle2 size={20} />
                </div>
                <div className="mobile-list-content">
                  <div className="mobile-list-title">Undvik skuggor</div>
                  <div className="mobile-list-subtitle">Hall telefonen rakt ovanfor</div>
                </div>
              </div>
            </div>
          </>
        ) : scanning ? (
          /* Scanning State */
          <div className="mobile-card" style={{ textAlign: 'center', padding: '40px 24px' }}>
            {preview && (
              <div style={{ 
                width: '200px', 
                height: '280px', 
                borderRadius: '12px', 
                overflow: 'hidden',
                margin: '0 auto 24px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }}>
                <img src={preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <div className="mobile-spinner" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ marginBottom: '8px' }}>Analyserar faktura...</h3>
            <p style={{ color: '#64748b', fontSize: '14px' }}>
              AI:n laser och extraherar information
            </p>
          </div>
        ) : result ? (
          /* Result State */
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#16a34a' }}>
                <CheckCircle2 size={20} />
                <span style={{ fontWeight: 600 }}>Faktura extraherad!</span>
              </div>
              <button 
                onClick={resetScan}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#64748b', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <X size={16} /> Avbryt
              </button>
            </div>

            {/* Preview thumbnail */}
            {preview && (
              <div className="mobile-card" style={{ padding: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '60px', 
                    height: '80px', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    flexShrink: 0
                  }}>
                    <img src={preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{result.supplier_name || 'Okand leverantor'}</div>
                    <div style={{ color: '#64748b', fontSize: '13px' }}>
                      Faktura #{result.invoice_number || '-'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Extracted Data */}
            <div className="mobile-card">
              <div className="mobile-card-header">
                <span className="mobile-card-title">Extraherad information</span>
              </div>
              
              <div style={{ display: 'grid', gap: '12px' }}>
                <div className="mobile-input-group" style={{ marginBottom: 0 }}>
                  <label className="mobile-label">Leverantor</label>
                  <div style={{ fontWeight: 500 }}>{result.supplier_name || '-'}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="mobile-input-group" style={{ marginBottom: 0 }}>
                    <label className="mobile-label">Fakturanummer</label>
                    <div style={{ fontWeight: 500 }}>{result.invoice_number || '-'}</div>
                  </div>
                  <div className="mobile-input-group" style={{ marginBottom: 0 }}>
                    <label className="mobile-label">Fakturadatum</label>
                    <div style={{ fontWeight: 500 }}>{result.invoice_date || '-'}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="mobile-input-group" style={{ marginBottom: 0 }}>
                    <label className="mobile-label">Forfallodatum</label>
                    <div style={{ fontWeight: 500 }}>{result.due_date || '-'}</div>
                  </div>
                  <div className="mobile-input-group" style={{ marginBottom: 0 }}>
                    <label className="mobile-label">Moms</label>
                    <div style={{ fontWeight: 500 }}>{result.vat_amount ? `${result.vat_amount} SEK` : '-'}</div>
                  </div>
                </div>
                <div className="mobile-input-group" style={{ marginBottom: 0 }}>
                  <label className="mobile-label">Totalbelopp</label>
                  <div style={{ fontWeight: 700, fontSize: '20px', color: '#1e3a5f' }}>
                    {result.total_amount ? `${result.total_amount} SEK` : '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button className="mobile-btn mobile-btn-outline" onClick={resetScan} style={{ flex: 1 }}>
                Skanna ny
              </button>
              <button className="mobile-btn mobile-btn-primary" onClick={saveAsSupplierInvoice} style={{ flex: 1 }}>
                Spara faktura
              </button>
            </div>
          </>
        ) : null}

        {/* Error State */}
        {error && (
          <div className="mobile-card" style={{ background: '#fee2e2', borderLeft: '4px solid #dc2626' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <AlertCircle size={24} color="#dc2626" />
              <div>
                <div style={{ fontWeight: 600, color: '#dc2626' }}>Fel vid skanning</div>
                <div style={{ fontSize: '14px', color: '#991b1b' }}>{error}</div>
              </div>
            </div>
            <button 
              className="mobile-btn mobile-btn-outline" 
              onClick={resetScan}
              style={{ marginTop: '12px' }}
            >
              Forsok igen
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
